import { useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { FLAGS, FeatureFlagKey } from 'utils/featureFlags';

const STORAGE_KEY = 'featureFlags';

type FeatureFlagsState = Record<FeatureFlagKey, boolean>;

/** Sync localStorage atom; Jotai types may include `Promise<>` for async storage. */
type FeatureFlagsSetter = (
  update: FeatureFlagsState | ((prev: FeatureFlagsState) => FeatureFlagsState),
) => void;

// Initialize with all flags set to false by default
const getDefaultFlags = (): FeatureFlagsState => {
  return Object.keys(FLAGS).reduce((acc, key) => {
    acc[key as FeatureFlagKey] = false;

    return acc;
  }, {} as FeatureFlagsState);
};

const storage = createJSONStorage(() => localStorage);

const featureFlagsAtom = atomWithStorage<FeatureFlagsState>(
  STORAGE_KEY,
  getDefaultFlags(),
  storage as any,
);

featureFlagsAtom.debugLabel = STORAGE_KEY;

/**
 * Hook to manage feature flags state
 * Feature flags are stored in localStorage and persist across sessions
 */
export const useFeatureFlags = () => {
  const [flags, setFlags] = useAtom(featureFlagsAtom) as [
    FeatureFlagsState,
    FeatureFlagsSetter,
  ];

  const toggleFlag = useCallback(
    (flagKey: FeatureFlagKey) => {
      setFlags((prev) => ({
        ...prev,
        [flagKey]: flagKey in prev ? !prev[flagKey] : false,
      }));
    },
    [setFlags],
  );

  const setFlag = useCallback(
    (flagKey: FeatureFlagKey, value: boolean) => {
      setFlags((prev) => ({
        ...prev,
        [flagKey]: value,
      }));
    },
    [setFlags],
  );

  const isFlagEnabled = useCallback(
    (flagKey: FeatureFlagKey): boolean => {
      if (flagKey === FLAGS.SCOUT_AGENT) {
        return true;
      }
      if (flagKey === FLAGS.ENRICH_CONTACTS) {
        return true;
      }

      return flags[flagKey] ?? false;
    },
    [flags],
  );

  const getFlagToken = useCallback(
    (flagKey: FeatureFlagKey): string | null => {
      if (flags[flagKey]) {
        return FLAGS[flagKey];
      }

      return null;
    },
    [flags],
  );

  const resetFlags = useCallback(() => {
    setFlags(getDefaultFlags());
  }, [setFlags]);

  return useMemo(
    () => ({
      flags,
      toggleFlag,
      setFlag,
      isFlagEnabled,
      getFlagToken,
      resetFlags,
    }),
    [flags, toggleFlag, setFlag, isFlagEnabled, getFlagToken, resetFlags],
  );
};
